import React, { ChangeEvent, useMemo, useState } from "react";
import "./PasteAddressModal.scss";
import OwButton from "../OwButton/OwButton";
import OwTitle from "../OwTitle/OwTitle";
import OwLabel from "../OwLabel/OwLabel";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import INewAddress from "../../../models/INewAddress";
import countriesWithLang from "../../../services/CountriesConstService";
import { parseAddress } from "../../../services/AddressParseService";
import * as _ from "lodash-es";

type Props = {
    closeModalFn: () => void;
    onApply: (parsed: Partial<INewAddress>) => void;
    initialText?: string;
};

export const PasteAddressModal: React.FC<Props> = (props) => {
    const [rawText, setRawText] = useState(props.initialText || "");
    const [hasParsed, setHasParsed] = useState(false);
    const [parsed, setParsed] = useState<Partial<INewAddress>>({});

    const countries = useMemo(() => {
        const extracted = countriesWithLang.countries.map((country) => ({
            title: country.name.common,
            value: country.cca3,
        }));

        const sorted = _.sortBy(extracted, (country) => country.title);

        // Bubble United States and Canada to the top, mirroring the checkout form ordering.
        const [common, rest] = _.partition(sorted, (country) => {
            switch (country.title) {
                case "United States":
                case "Canada":
                    return true;
                default:
                    return false;
            }
        });

        const ordered = common.concat(rest);
        if (ordered.length > 1) {
            const usa = ordered[1];
            const canada = ordered[0];
            ordered[0] = usa;
            ordered[1] = canada;
        }

        return ordered;
    }, []);

    const closeModal = () => {
        props.closeModalFn();
    };

    const parse = () => {
        setParsed(parseAddress(rawText));
        setHasParsed(true);
    };

    const apply = () => {
        props.onApply(parsed);
        props.closeModalFn();
    };

    const handleTextChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        setRawText(e.target.value);
    };

    const handleFieldChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        setParsed((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    return (
        <div className={"PasteAddressModal p-2"}>
            <div className={"w-100"}>
                <div className={"d-flex justify-content-between"}>
                    <OwTitle leftIconName={"fad fa-paste"} title={"paste_address"} tag={"h3"} />
                    <div>
                        <OwButton className={"btn-danger"} centerIconClass={"fas fa-times"} fn={closeModal} />
                    </div>
                </div>
            </div>

            <div>
                <OwLabel title={"paste_address_here"} />
                <textarea
                    className={"form-control"}
                    rows={6}
                    value={rawText}
                    onChange={handleTextChange}
                    data-testid={"paste-address-textarea"}
                />
                <div className={"mt-2"}>
                    <OwButton title={"parse"} fn={parse} disabled={!rawText.trim().length} />
                </div>
            </div>

            {hasParsed ? (
                <div className={"mt-3"}>
                    <OwLabel title={"review_parsed_address"} isBold={true} />

                    <div className={"mt-2"}>
                        <OwLabel title={"name"} />
                        <OwFnInput fn={handleFieldChange} name={"name"} value={parsed.name} />
                    </div>
                    <div>
                        <OwLabel title={"company_name"} />
                        <OwFnInput fn={handleFieldChange} name={"company_name"} value={parsed.company_name} />
                    </div>
                    <div>
                        <OwLabel title={"address_line_one"} />
                        <OwFnInput fn={handleFieldChange} name={"address_line_one"} value={parsed.address_line_one} />
                    </div>
                    <div>
                        <OwLabel title={"address_line_two"} />
                        <OwFnInput fn={handleFieldChange} name={"address_line_two"} value={parsed.address_line_two} />
                    </div>
                    <div>
                        <OwLabel title={"address_line_three"} />
                        <OwFnInput fn={handleFieldChange} name={"address_line_three"} value={parsed.address_line_three} />
                    </div>
                    <div>
                        <OwLabel title={"city"} />
                        <OwFnInput fn={handleFieldChange} name={"city"} value={parsed.city} />
                    </div>
                    <div>
                        <OwLabel title={"state"} />
                        <OwFnInput fn={handleFieldChange} name={"state"} value={parsed.state} />
                    </div>
                    <div>
                        <OwLabel title={"postal_code"} />
                        <OwFnInput fn={handleFieldChange} name={"postal_code"} value={parsed.postal_code} />
                    </div>
                    <div>
                        <OwLabel title={"country"} />
                        <OwSelectOption
                            name={"country"}
                            fn={handleFieldChange}
                            options={countries}
                            selectedOption={parsed}
                            selectedOptionProp={"country"}
                            valueProp={"value"}
                            titleProp={"title"}
                        />
                    </div>

                    <div className={"mt-3"}>
                        <OwButton title={"apply"} fn={apply} />
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default PasteAddressModal;
